import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { api } from '../../lib/api';
import { getUpgradeCopy, type SubscriptionFeature } from '../../lib/subscriptionGate';
import { useAuth } from '../../context/AuthContext';

type Props = {
  open: boolean;
  feature: SubscriptionFeature;
  onClose: () => void;
};

export default function SubscriptionRequiredModal({ open, feature, onClose }: Props) {
  const navigate = useNavigate();
  const { me } = useAuth();
  const copy = useMemo(() => getUpgradeCopy(feature), [feature]);
  const [isSending, setIsSending] = useState(false);
  const isUnder16Student = me?.role === 'elev' && !Boolean(me?.over16);
  const [isCoveredByParentActive, setIsCoveredByParentActive] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setIsCoveredByParentActive(false);

    if (!isUnder16Student) return () => {};

    (async () => {
      try {
        const resp = await api.get('/api/subscriptions/me');
        const plan = String((resp.data as any)?.plan || 'free').toLowerCase();
        const unrestricted = Boolean((resp.data as any)?.unrestricted);
        const covered = Boolean((resp.data as any)?.covered_by_parent);
        const ok = Boolean(unrestricted || (covered && (plan === 'basic' || plan === 'full')));
        if (!cancelled) setIsCoveredByParentActive(ok);
      } catch {
        if (!cancelled) setIsCoveredByParentActive(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isUnder16Student, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/40 p-4" onMouseDown={onClose}>
      <div
        className="w-full max-w-lg bg-card border border-border rounded-xl p-5"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-foreground">{copy.title}</div>
            <div className="text-sm text-muted-foreground mt-1">
              {isCoveredByParentActive
                ? 'Contul tău este deja acoperit de abonamentul părintelui. Dacă tot vezi acest mesaj, reîncarcă pagina.'
                : copy.body}
            </div>
          </div>
          <button
            className="w-9 h-9 rounded-md hover:bg-muted flex items-center justify-center"
            onClick={onClose}
            aria-label="Închide"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Închide
          </Button>
          {isUnder16Student && !isCoveredByParentActive ? (
            <Button
              variant="default"
              iconName="Bell"
              loading={isSending}
              onClick={async () => {
                if (isSending) return;
                setIsSending(true);
                try {
                  const resp = await api.post('/api/users/me/subscription-access-request', {
                    feature,
                    context: copy.title,
                    link: '/subscriptions',
                  });
                  onClose();
                  const sent = Number((resp.data as any)?.sent ?? 0);
                  window.alert(
                    sent > 0
                      ? 'Am trimis o notificare catre parinti. Ei pot activa abonamentul pentru a debloca aceasta functionalitate.'
                      : 'Cererea a fost deja trimisa recent catre parinti.'
                  );
                } catch (e: any) {
                  const msg =
                    e?.response?.data?.detail || e?.message || 'Nu am putut trimite cererea catre parinti.';
                  window.alert(msg);
                } finally {
                  setIsSending(false);
                }
              }}
            >
              Solicita acces parintilor
            </Button>
          ) : (
            <Button
              variant="default"
              iconName="CreditCard"
              onClick={() => {
                onClose();
                navigate('/subscriptions');
              }}
            >
              Vezi abonamente
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
